import { useEffect, useRef, useState } from 'react';
import type { HardwarePhotoCrop } from '@cer-autofill/shared';

function CropPercentage({
  value,
  min,
  label,
  onChange,
}: {
  value: number;
  min: number;
  label: string;
  onChange: (value: number) => void;
}) {
  const [text, setText] = useState(String(value));
  useEffect(() => setText(String(value)), [value]);
  return (
    <input
      aria-label={label}
      type="number"
      min={min}
      max="100"
      step="1"
      value={text}
      onChange={(event) => {
        const next = event.target.value;
        setText(next);
        if (next !== '' && Number(next) >= min && Number(next) <= 100) onChange(Number(next));
      }}
      onBlur={() => {
        if (text === '' || !Number.isFinite(Number(text))) {
          setText(String(value));
          return;
        }
        const next = Math.max(min, Math.min(100, Number(text)));
        setText(String(next));
        onChange(next);
      }}
    />
  );
}

export function LabelPhotoCrop({
  image,
  zoom,
  crop,
  selecting,
  onCrop,
  onLoad,
  onError,
}: {
  image: string;
  zoom: number;
  crop: HardwarePhotoCrop | undefined;
  selecting: boolean;
  onCrop: (crop: HardwarePhotoCrop | undefined) => void;
  onLoad: () => void;
  onError: () => void;
}) {
  const [preview, setPreview] = useState<HardwarePhotoCrop>();
  const selected = preview ?? crop;
  const start = useRef<{ x: number; y: number } | null>(null);
  function point(event: React.PointerEvent<HTMLDivElement>) {
    const box = event.currentTarget.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(1, (event.clientX - box.left) / box.width)),
      y: Math.max(0, Math.min(1, (event.clientY - box.top) / box.height)),
    };
  }
  return (
    <>
      <div className="label-suggestions__image">
        <div
          className={`label-crop${selecting ? ' label-crop--selecting' : ''}`}
          style={{ width: `${zoom * 100}%` }}
          onPointerDown={(event) => {
            if (!selecting) return;
            event.preventDefault();
            start.current = point(event);
            event.currentTarget.setPointerCapture(event.pointerId);
          }}
          onPointerMove={(event) => {
            if (!start.current) return;
            const end = point(event);
            setPreview({
              x: Math.min(start.current.x, end.x),
              y: Math.min(start.current.y, end.y),
              width: Math.abs(end.x - start.current.x),
              height: Math.abs(end.y - start.current.y),
            });
          }}
          onPointerUp={(event) => {
            if (!start.current) return;
            const end = point(event),
              origin = start.current;
            start.current = null;
            setPreview(undefined);
            const width = Math.abs(end.x - origin.x),
              height = Math.abs(end.y - origin.y);
            if (width >= 0.02 && height >= 0.02)
              onCrop({ x: Math.min(origin.x, end.x), y: Math.min(origin.y, end.y), width, height });
            event.currentTarget.releasePointerCapture(event.pointerId);
          }}
          onPointerCancel={() => {
            start.current = null;
            setPreview(undefined);
          }}
        >
          <img
            src={image}
            alt="Selected hardware evidence"
            draggable={false}
            onLoad={onLoad}
            onError={onError}
          />
          {selected && (
            <div
              className="label-crop__selection"
              aria-label="Selected label region"
              style={{
                left: `${selected.x * 100}%`,
                top: `${selected.y * 100}%`,
                width: `${selected.width * 100}%`,
                height: `${selected.height * 100}%`,
              }}
            />
          )}
        </div>
      </div>
      {selecting && (
        <div className="label-crop__controls">
          <span className="muted">Drag around the label, or set its area below.</span>
          {(['x', 'y', 'width', 'height'] as const).map((key) => (
            <label key={key}>
              {{ x: 'Left', y: 'Top', width: 'Width', height: 'Height' }[key]} (%)
              <CropPercentage
                label={`Label crop ${key}`}
                min={key === 'x' || key === 'y' ? 0 : 2}
                value={Math.round((crop?.[key] ?? { x: 0, y: 0, width: 1, height: 1 }[key]) * 100)}
                onChange={(percent) => {
                  const next = {
                    x: 0,
                    y: 0,
                    width: 1,
                    height: 1,
                    ...crop,
                    [key]: percent / 100,
                  };
                  next.x = Math.max(0, Math.min(0.98, next.x));
                  next.y = Math.max(0, Math.min(0.98, next.y));
                  next.width = Math.max(0.02, Math.min(1 - next.x, next.width));
                  next.height = Math.max(0.02, Math.min(1 - next.y, next.height));
                  onCrop(next);
                }}
              />
            </label>
          ))}
        </div>
      )}
    </>
  );
}
